import React from "react";
import "./DatabaseSkeleton.css";

/**
 * Skeleton pulse animation component
 */
const SkeletonPulse = ({ width, height, borderRadius = "4px", className = "" }) => (
    <div
        className={`skeleton-pulse ${className}`}
        style={{ width, height, borderRadius }}
    />
);

/**
 * Skeleton for stat cards on listing page
 */
export const StatsSkeleton = () => (
    <div className="my-machine-top-boxes-wrapper">
        {[1, 2, 3, 4].map((i) => (
            <div key={i} className="my-machine-top-box-single scheduler-status-box skeleton-stat-card">
                <div>
                    <SkeletonPulse width="48px" height="36px" borderRadius="4px" />
                </div>
                <SkeletonPulse width="80px" height="14px" className="mt-2" />
            </div>
        ))}
    </div>
);

/**
 * Skeleton for database card in card view
 */
export const DatabaseCardSkeleton = () => (
    <div className="database-card skeleton-card">
        {/* Card Header */}
        <div className="database-card-header">
            <div className="database-card-title-row">
                <SkeletonPulse width="48px" height="48px" borderRadius="8px" />
                <div className="database-card-info">
                    <SkeletonPulse width="140px" height="18px" />
                    <div className="database-card-meta mt-2">
                        <SkeletonPulse width="70px" height="20px" borderRadius="10px" />
                        <SkeletonPulse width="40px" height="16px" className="ms-2" />
                        <SkeletonPulse width="65px" height="20px" borderRadius="10px" className="ms-2" />
                    </div>
                </div>
            </div>
        </div>

        {/* Card Body */}
        <div className="database-card-body">
            {[1, 2, 3, 4].map((i) => (
                <div key={i} className="database-card-row">
                    <SkeletonPulse width="70px" height="12px" />
                    <SkeletonPulse width={i === 1 ? "180px" : "100px"} height="14px" />
                </div>
            ))}
        </div>

        {/* Card Footer */}
        <div className="database-card-footer">
            <SkeletonPulse width="120px" height="12px" />
            <div className="database-card-actions">
                <SkeletonPulse width="28px" height="28px" borderRadius="4px" />
                <SkeletonPulse width="28px" height="28px" borderRadius="4px" />
                <SkeletonPulse width="28px" height="28px" borderRadius="4px" />
            </div>
        </div>
    </div>
);

/**
 * Grid of skeleton cards
 */
export const DatabaseCardsGridSkeleton = ({ count = 6 }) => (
    <div className="database-cards-grid">
        {Array.from({ length: count }).map((_, i) => (
            <DatabaseCardSkeleton key={i} />
        ))}
    </div>
);

/**
 * Skeleton for table row
 */
export const DatabaseTableRowSkeleton = () => (
    <tr className="skeleton-row">
        <td>
            <div className="d-flex align-items-center gap-2">
                <SkeletonPulse width="32px" height="32px" borderRadius="6px" />
                <SkeletonPulse width="130px" height="16px" />
            </div>
        </td>
        <td><SkeletonPulse width="80px" height="20px" borderRadius="10px" /></td>
        <td><SkeletonPulse width="60px" height="20px" borderRadius="10px" /></td>
        <td><SkeletonPulse width="180px" height="14px" /></td>
        <td><SkeletonPulse width="50px" height="14px" /></td>
        <td><SkeletonPulse width="90px" height="14px" /></td>
        <td>
            <div className="d-flex gap-2">
                <SkeletonPulse width="28px" height="28px" borderRadius="4px" />
                <SkeletonPulse width="28px" height="28px" borderRadius="4px" />
                <SkeletonPulse width="28px" height="28px" borderRadius="4px" />
            </div>
        </td>
    </tr>
);

/**
 * Skeleton for table
 */
export const DatabaseTableSkeleton = ({ rows = 5 }) => (
    <div className="scheduler-table-main database-table-main">
        <div className="table-wrapper-main">
            <table className="table common-table">
                <thead>
                    <tr>
                        <th><SkeletonPulse width="60px" height="14px" /></th>
                        <th><SkeletonPulse width="50px" height="14px" /></th>
                        <th><SkeletonPulse width="50px" height="14px" /></th>
                        <th><SkeletonPulse width="60px" height="14px" /></th>
                        <th><SkeletonPulse width="50px" height="14px" /></th>
                        <th><SkeletonPulse width="60px" height="14px" /></th>
                        <th><SkeletonPulse width="50px" height="14px" /></th>
                    </tr>
                </thead>
                <tbody>
                    {Array.from({ length: rows }).map((_, i) => (
                        <DatabaseTableRowSkeleton key={i} />
                    ))}
                </tbody>
            </table>
        </div>
    </div>
);

/**
 * Skeleton for database detail header
 */
export const DatabaseDetailHeaderSkeleton = () => (
    <div className="db-detail-header skeleton-detail-header">
        <div className="db-detail-header-left">
            <SkeletonPulse width="64px" height="64px" borderRadius="12px" />
            <div className="db-detail-header-info">
                <SkeletonPulse width="200px" height="24px" className="mb-2" />
                <div className="d-flex gap-2 align-items-center">
                    <SkeletonPulse width="90px" height="24px" borderRadius="12px" />
                    <SkeletonPulse width="45px" height="20px" />
                    <SkeletonPulse width="70px" height="24px" borderRadius="12px" />
                </div>
            </div>
        </div>
        <div className="db-detail-header-actions">
            <SkeletonPulse width="90px" height="36px" borderRadius="4px" />
            <SkeletonPulse width="90px" height="36px" borderRadius="4px" />
            <SkeletonPulse width="90px" height="36px" borderRadius="4px" />
        </div>
    </div>
);

/**
 * Skeleton for metric cards
 */
export const MetricCardSkeleton = () => (
    <div className="db-detail-metric-card skeleton-metric-card">
        <div className="metric-header">
            <SkeletonPulse width="24px" height="24px" borderRadius="4px" />
            <SkeletonPulse width="60px" height="14px" />
        </div>
        <div className="metric-body">
            <SkeletonPulse width="80px" height="32px" />
        </div>
        <SkeletonPulse width="100%" height="6px" borderRadius="3px" className="mt-2" />
    </div>
);

/**
 * Grid of metric card skeletons
 */
export const MetricsGridSkeleton = ({ count = 4 }) => (
    <div className="db-detail-metrics-grid">
        {Array.from({ length: count }).map((_, i) => (
            <MetricCardSkeleton key={i} />
        ))}
    </div>
);

/**
 * Skeleton for connection info section
 */
export const ConnectionInfoSkeleton = () => (
    <div className="db-detail-connection-section skeleton-connection">
        <div className="section-header">
            <SkeletonPulse width="120px" height="18px" />
        </div>
        <div className="connection-details-grid">
            {[1, 2, 3, 4, 5].map((i) => (
                <div key={i} className="connection-detail-item">
                    <SkeletonPulse width="80px" height="12px" className="mb-2" />
                    <SkeletonPulse width={i === 1 ? "100%" : "140px"} height="16px" />
                </div>
            ))}
        </div>
        <div className="connection-string-section mt-3">
            <SkeletonPulse width="120px" height="14px" className="mb-2" />
            <SkeletonPulse width="100%" height="40px" borderRadius="4px" />
        </div>
    </div>
);

/**
 * Skeleton for tabs content
 */
export const TabContentSkeleton = () => (
    <div className="skeleton-tab-content">
        <div className="mb-4">
            <SkeletonPulse width="160px" height="20px" className="mb-3" />
            <div className="skeleton-table">
                {[1, 2, 3, 4, 5, 6].map((i) => (
                    <div key={i} className="skeleton-table-row">
                        <SkeletonPulse width="120px" height="14px" />
                        <SkeletonPulse width="200px" height="14px" />
                    </div>
                ))}
            </div>
        </div>
    </div>
);

/**
 * Full database detail page skeleton
 */
export const DatabaseDetailSkeleton = () => (
    <div className="db-detail-skeleton">
        {/* Header Card */}
        <div className="db-detail-header skeleton-header-card">
            <div className="db-detail-header-left">
                <div className="db-detail-icon">
                    <SkeletonPulse width="56px" height="56px" borderRadius="12px" />
                </div>
                <div className="db-detail-info">
                    <SkeletonPulse width="180px" height="28px" className="mb-2" />
                    <div className="db-detail-meta">
                        <SkeletonPulse width="100px" height="20px" borderRadius="4px" />
                        <SkeletonPulse width="80px" height="24px" borderRadius="12px" className="ms-2" />
                    </div>
                </div>
            </div>
            <div className="db-detail-header-right">
                <SkeletonPulse width="85px" height="32px" borderRadius="4px" />
                <SkeletonPulse width="70px" height="32px" borderRadius="4px" />
                <SkeletonPulse width="80px" height="32px" borderRadius="4px" />
                <SkeletonPulse width="75px" height="32px" borderRadius="4px" />
            </div>
        </div>

        {/* Quick Stats Row - 4 Metric Cards */}
        <div className="db-stats-row row">
            {[1, 2, 3, 4].map((i) => (
                <div key={i} className="col-6 col-lg-3">
                    <div className="db-detail-metric-card skeleton-metric-card">
                        <div className="metric-header">
                            <SkeletonPulse width="22px" height="22px" borderRadius="4px" />
                            <SkeletonPulse width="80px" height="14px" />
                        </div>
                        <div className="metric-body">
                            <SkeletonPulse width="60px" height="32px" />
                            <SkeletonPulse width="30px" height="16px" className="ms-1" />
                        </div>
                        <SkeletonPulse width="120px" height="12px" className="mt-1" />
                        <SkeletonPulse width="100%" height="6px" borderRadius="3px" className="mt-2" />
                    </div>
                </div>
            ))}
        </div>

        {/* Tabs Navigation */}
        <div className="db-detail-tabs">
            <div className="db-nav-tabs nav nav-tabs">
                {["Overview", "Connectivity", "Configuration", "Backups"].map((tab, i) => (
                    <div key={i} className="nav-item">
                        <div className="nav-link skeleton-tab-link">
                            <SkeletonPulse width="16px" height="16px" borderRadius="2px" />
                            <SkeletonPulse width={`${tab.length * 7}px`} height="14px" className="ms-2" />
                        </div>
                    </div>
                ))}
            </div>

            {/* Tab Content */}
            <div className="db-tab-content tab-content">
                <div className="tab-pane active">
                    <div className="row">
                        {/* Left Column - Database Information */}
                        <div className="col-lg-6">
                            <div className="info-section skeleton-info-section">
                                <SkeletonPulse width="160px" height="16px" className="mb-3" />
                                <div className="db-info-table">
                                    {[1, 2, 3, 4, 5].map((i) => (
                                        <div key={i} className="skeleton-info-row">
                                            <SkeletonPulse width="120px" height="14px" />
                                            <SkeletonPulse width={i === 2 ? "200px" : "140px"} height="14px" />
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                        
                        {/* Right Column - Resource Summary */}
                        <div className="col-lg-6">
                            <div className="info-section skeleton-info-section">
                                <SkeletonPulse width="140px" height="16px" className="mb-3" />
                                <div className="db-info-table">
                                    {[1, 2, 3, 4, 5, 6, 7].map((i) => (
                                        <div key={i} className="skeleton-info-row">
                                            <SkeletonPulse width="110px" height="14px" />
                                            <SkeletonPulse width="100px" height="14px" />
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
);

/**
 * Skeleton for create database form
 */
export const CreateDatabaseSkeleton = () => (
    <div className="create-db-skeleton">
        {/* Steps indicator */}
        <div className="steps-skeleton mb-4">
            <div className="d-flex justify-content-center gap-4">
                {[1, 2, 3].map((i) => (
                    <div key={i} className="step-skeleton-item">
                        <SkeletonPulse width="32px" height="32px" borderRadius="50%" />
                        <SkeletonPulse width="80px" height="12px" className="mt-2" />
                    </div>
                ))}
            </div>
        </div>

        {/* Form content */}
        <div className="form-skeleton">
            <SkeletonPulse width="180px" height="24px" className="mb-4" />
            
            {/* Form fields */}
            {[1, 2, 3, 4].map((i) => (
                <div key={i} className="form-field-skeleton mb-3">
                    <SkeletonPulse width="100px" height="14px" className="mb-2" />
                    <SkeletonPulse width="100%" height="40px" borderRadius="4px" />
                </div>
            ))}

            {/* Buttons */}
            <div className="d-flex justify-content-end gap-2 mt-4">
                <SkeletonPulse width="100px" height="40px" borderRadius="4px" />
                <SkeletonPulse width="120px" height="40px" borderRadius="4px" />
            </div>
        </div>
    </div>
);

const DatabaseSkeletons = {
    StatsSkeleton,
    DatabaseCardSkeleton,
    DatabaseCardsGridSkeleton,
    DatabaseTableRowSkeleton,
    DatabaseTableSkeleton,
    DatabaseDetailHeaderSkeleton,
    MetricCardSkeleton,
    MetricsGridSkeleton,
    ConnectionInfoSkeleton,
    TabContentSkeleton,
    DatabaseDetailSkeleton,
    CreateDatabaseSkeleton,
};

export default DatabaseSkeletons;
